import React, { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import ModalContent from '../modals/content';
import { useAuth } from '../../hooks/use-auth';
import CommonAnimatedSpinner from '../common/animated/spinner';
import StyledInput from '../common/form/styled-input';
import StyledLabel from '../common/form/styled-label';
import * as customersService from '../../services/api/customers';
import CommonButtonDefault from '../common/buttons/default';
import AlertPrimary from '../common/alerts/alert-primary';

interface UserForm {
  email: string;
}

interface ComponentProps {
  userId: number;
  currEmail: string;
  onUpdateEmail: (email: string) => void;
}

function EditEmailModal({ userId, currEmail, onUpdateEmail }: ComponentProps) {
  const { token } = useAuth();
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { register, handleSubmit } = useForm<UserForm>();
  const [error, setError] = useState<string>();

  const onDisplayModal = () => {
    setIsModalVisible(true);
  };

  const onSubmit: SubmitHandler<UserForm> = async (data) => {
    try {
      setError(undefined); // Clear previous errors
      setIsLoading(true);
      const res = await customersService.update(token, userId, { email: data.email });

      if (res) {
        setIsLoading(false);
        setIsModalVisible(false);
        onUpdateEmail(data.email);
      } else {
        setIsLoading(false);
        setError('Unexpected response from the server.');
      }
    } catch (e) {
      setIsLoading(false);
      setError((e as Error).message || 'An unknown error occurred.');
    }
  };

  return (
    <div>
      {error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 my-5">
          <AlertPrimary text={error} />
        </div>
      )}
      <CommonButtonDefault outline type="button" onClick={() => onDisplayModal()}>
        Change Email
      </CommonButtonDefault>
      {isModalVisible && (
        <ModalContent visible onCloseClick={() => setIsModalVisible(false)} title="Change email">
          <div className="mt-7">
            <span className="text-gray-500">Current buyer’s email: </span>
            {currEmail}
          </div>
          <div className="mt-3">
            <StyledLabel>New email:</StyledLabel>
            <StyledInput {...register('email', { required: true })} />
            <div className="pt-5">
              <div className="flex">
                <button
                  type="button"
                  disabled={isLoading}
                  onClick={handleSubmit(onSubmit)}
                  className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Save
                </button>
                <button
                  onClick={() => setIsModalVisible(false)}
                  type="button"
                  className="ml-3 bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Cancel
                </button>
                {isLoading && (
                  <div className="mt-2 pl-3">
                    <CommonAnimatedSpinner sm />
                  </div>
                )}
              </div>
            </div>
          </div>
        </ModalContent>
      )}
    </div>
  );
}

export default EditEmailModal;
